import { Alert, Card, Flex, Tag, Typography } from 'antd';

import ColorDot from '@components/modal/objectPickerModal/ColorDot';
import { formatAoiArea } from '@utils/aoiStats';
import { DETECTION_OUTCOME } from '@utils/detectionOutcome';

const { Text, Title } = Typography;

const SummaryRow = ({ label, children }) => (
	<Flex vertical gap={6}>
		<Text type="secondary" style={{ fontSize: 11, letterSpacing: 1.5, fontWeight: 600 }}>
			{label}
		</Text>
		{children}
	</Flex>
);

// Per-outcome messaging. Kept here so the wizard's Run step and the map's
// boundary-edit dialog always say the same thing about the same outcome.
//
// Only NEEDS_CONFIRMATION appears: the other two outcomes never render a summary.
// A started run closes its surface, and an already-detected area is handled with
// a toast rather than a dialog, since there's nothing to decide.
const OUTCOME_ALERTS = {
	[DETECTION_OUTCOME.NEEDS_CONFIRMATION]: {
		type: 'warning',
		// Says why confirmation is being asked for and what it costs. The
		// instruction to confirm is deliberately left out: the heading, the
		// description and the primary button all carry it already.
		title: "This run covers ground that hasn't been analyzed yet, so it may take a while to complete.",
	},
};

const DEFAULT_ALERT = {
	type: 'success',
	title: 'Detecting objects is a resource-intensive process. You will be notified when the run is complete.',
};

/**
 * Presentational review of a detection run: what will be detected, where, and
 * how much ground it would cover. Purely props-driven so it can be reused by
 * both detection entry points — the Object Picker's Run step (which feeds it
 * from the picker store) and the map's boundary-edit confirmation dialog (which
 * feeds it from the collection currently rendered on the map).
 *
 * Rows render only when the matching prop has a value, so callers can show a
 * lighter or fuller summary without extra flags.
 *
 * @param {object} props
 * @param {string} [props.collectionName]      - name of the collection being run
 * @param {Array<{key: string, label: string, color?: string}>} [props.objects] - objects the run covers
 * @param {string} [props.areaLabel]           - human description of the run's area
 * @param {import('react').ReactNode} [props.areaIcon] - icon shown beside `areaLabel`
 * @param {number|null} [props.scanAreaSquareMeters] - ground a run would scan, in
 *   square metres, from the server's `run_preview`. Displayed in a unit scaled to
 *   its size. Omitted when the backend can't measure it.
 * @param {string} [props.outcome]             - a {@link DETECTION_OUTCOME} value; picks the alert
 * @param {string} [props.heading]             - title above the card
 * @param {string} [props.description]         - supporting copy under the heading
 * @param {boolean} [props.showAlert=true]     - set false to drop the trailing alert
 */
const DetectionSummary = ({
	collectionName,
	objects,
	areaLabel,
	areaIcon,
	scanAreaSquareMeters = null,
	outcome,
	heading,
	description,
	showAlert = true,
}) => {
	const trimmedName = collectionName?.trim();
	const hasObjects = Boolean(objects?.length);
	const alert = OUTCOME_ALERTS[outcome] ?? DEFAULT_ALERT;
	// `null` for anything unmeasurable, which also drops the row.
	const scanAreaLabel = formatAoiArea(scanAreaSquareMeters);

	return (
		<Flex vertical gap={20}>
			{/* Grouped as one child of the outer Flex so its `gap` separates this
			    block from the Card, not the heading from its own description. The
			    inner gap is small and deliberate: `Title` sets margin 0, so a bare
			    wrapper leaves the two lines touching. */}
			{(heading || description) && (
				<Flex vertical gap={4}>
					{heading && (
						<Title level={5} style={{ margin: 0 }}>
							{heading}
						</Title>
					)}
					{description && (
						<Text type="secondary" style={{ fontSize: 13 }}>
							{description}
						</Text>
					)}
				</Flex>
			)}

			<Card size="small">
				<Flex vertical gap={16}>
					{trimmedName && (
						<SummaryRow label="COLLECTION">
							<Text strong>{trimmedName}</Text>
						</SummaryRow>
					)}

					{hasObjects && (
						<SummaryRow label={`OBJECTS (${objects.length})`}>
							<Flex gap={8} wrap>
								{objects.map(node => (
									<Tag
										key={node.key}
										variant="outlined"
										style={{
											margin: 0,
											display: 'inline-flex',
											alignItems: 'center',
											gap: 6,
										}}>
										<ColorDot color={node.color} />
										{node.label}
									</Tag>
								))}
							</Flex>
						</SummaryRow>
					)}

					{areaLabel && (
						<SummaryRow label="AREA">
							<Flex align="center" gap={8}>
								{areaIcon}
								<Text>{areaLabel}</Text>
							</Flex>
						</SummaryRow>
					)}

					{scanAreaLabel && (
						<SummaryRow label="GROUND TO SCAN">
							<Text>{scanAreaLabel}</Text>
						</SummaryRow>
					)}
				</Flex>
			</Card>

			{showAlert && <Alert type={alert.type} showIcon title={alert.title} />}
		</Flex>
	);
};

export default DetectionSummary;
